import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { useAuth } from '../../auth/AuthProvider'
import { useSalvamento } from '../../componentes/useSalvamento'
import BotaoSalvar from '../../componentes/BotaoSalvar'
import QuadroDaImagem from '../../componentes/QuadroDaImagem'
import { registrarGuardaDeSaida, podeSair } from '../../componentes/guardaDeSaida'
import { confirmar } from '../../componentes/confirmar'
import { buscarEmpresa, salvarEmpresa, EMPRESA_VAZIA, type CamposEmpresa } from '../../empresa/empresa'
import { validarNovaSenha } from '../../lib/senha'

// Spec 2026-10-06 (dados da empresa + foto/logo), no visual do exemplo dela:
// cartao "Dados da empresa" (imagem + dados que saem no PDF, tabela `empresas`)
// e cartao "Seus dados de usuario" (nome/telefone/e-mail comercial seguem em
// user_metadata, como desde 09/09; e-mail de login so leitura; Trocar senha).
// Com ?inicio=1 e o "Cadastro inicial" logo depois de criar a conta.
const CAMPO = 'w-full border border-slate-300 rounded-lg px-3 py-2'
const ROTULO = 'block text-sm text-slate-600 mb-1'
const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR',
  'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']

type CampoTexto = Exclude<keyof CamposEmpresa, 'logo'>

function normalizar(e: CamposEmpresa): CamposEmpresa {
  const limpo = (v: string | null) => (v ?? '').trim() || null
  return {
    razao_social: limpo(e.razao_social), nome_fantasia: limpo(e.nome_fantasia), cnpj: limpo(e.cnpj),
    email: limpo(e.email), telefone: limpo(e.telefone), endereco: limpo(e.endereco), cep: limpo(e.cep),
    municipio: limpo(e.municipio), uf: limpo(e.uf), logo: e.logo,
  }
}

function Cartao({ icone, titulo, subtitulo, testId, children }: {
  icone: string; titulo: string; subtitulo: string; testId: string; children: React.ReactNode
}) {
  return (
    <div data-testid={testId} className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 bg-teal-50/60 p-3">
        <span className="w-10 h-10 shrink-0 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center" aria-hidden="true">{icone}</span>
        <div className="min-w-0">
          <h3 className="font-bold text-slate-800">{titulo}</h3>
          <p className="text-xs text-slate-500">{subtitulo}</p>
        </div>
      </div>
      <div className="p-3 space-y-3">{children}</div>
    </div>
  )
}

export default function MeuPerfil() {
  const navegar = useNavigate()
  const [params] = useSearchParams()
  const inicio = params.get('inicio') === '1'
  const { session, atualizarPerfil, salvarNovaSenha, conferirSenhaAtual } = useAuth()
  const { estado, executar, reiniciar } = useSalvamento()

  const [nome, setNome] = useState('')
  const [emailComercial, setEmailComercial] = useState('')
  const [telefone, setTelefone] = useState('')
  const [empresa, setEmpresa] = useState<CamposEmpresa>(EMPRESA_VAZIA)
  const [sujo, setSujo] = useState(false)

  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [senhaAtual, setSenhaAtual] = useState('')
  const [novaSenha, setNovaSenha] = useState('')
  const [repetirSenha, setRepetirSenha] = useState('')
  const [avisoSenha, setAvisoSenha] = useState<{ texto: string; ok: boolean } | null>(null)

  // Revisao final: o SIGNED_IN da conferencia de senha e o USER_UPDATED trocam
  // a sessao -- nao podem apagar o que ela digitou e ainda nao salvou.
  useEffect(() => {
    if (sujo) return
    const metadata = session?.user.user_metadata
    setNome(typeof metadata?.nome === 'string' ? metadata.nome : '')
    setEmailComercial(typeof metadata?.email_comercial === 'string' ? metadata.email_comercial : '')
    setTelefone(typeof metadata?.telefone === 'string' ? metadata.telefone : '')
  }, [session, sujo])

  // Revisao final: reaplica o que esta salvo SEMPRE que nao ha rascunho -- o
  // sync que chega depois de abrir a tela nao pode ser ignorado (senao o Salvar
  // gravaria o formulario vazio por cima da empresa do servidor).
  const usuarioId = session?.user.id
  const salva = useLiveQuery(() => buscarEmpresa(usuarioId).then((e) => e ?? null), [usuarioId])
  const camposSalvos = (e: typeof salva): CamposEmpresa => {
    if (!e) return EMPRESA_VAZIA
    const { id: _i, criado_em: _c, atualizado_em: _a, apagado_em: _p, ...campos } = e
    return { ...EMPRESA_VAZIA, ...campos }
  }
  useEffect(() => {
    if (sujo || salva === undefined) return
    setEmpresa(camposSalvos(salva))
  }, [salva, sujo])
  const [avisoSalvar, setAvisoSalvar] = useState<string | null>(null)

  useEffect(() => {
    if (!sujo) return undefined
    return registrarGuardaDeSaida(() => confirmar('Descartar as alterações não salvas?', { botao: 'Descartar', perigo: true }))
  }, [sujo])

  function mexeu() { reiniciar(); setSujo(true) }
  function mudar(definir: (valor: string) => void) {
    return (valor: string) => { mexeu(); definir(valor) }
  }
  function mudarEmpresa(parte: Partial<CamposEmpresa>) {
    mexeu()
    setEmpresa((atual) => ({ ...atual, ...parte }))
  }

  async function salvar() {
    // A empresa grava local + outbox (funciona sem internet); o user_metadata
    // precisa de rede -- se falhar, a empresa ja ficou salva e a mensagem diz.
    if (!usuarioId) throw new Error('Sessão expirada. Entre de novo.')
    setAvisoSalvar(null)
    const novaEmpresa = normalizar(empresa)
    // So grava (e enfileira o logo) quando a empresa mudou.
    if (JSON.stringify(novaEmpresa) !== JSON.stringify(normalizar(camposSalvos(salva)))) {
      await salvarEmpresa(novaEmpresa, usuarioId)
    }
    // Revisao final: o user_metadata precisa de internet -- so chama quando
    // nome/telefone/e-mail comercial mudaram (mexer so na empresa salva offline).
    const meta = session?.user.user_metadata
    const texto = (v: unknown) => (typeof v === 'string' ? v : '')
    const mudouUsuario = nome !== texto(meta?.nome) || emailComercial !== texto(meta?.email_comercial)
      || telefone !== texto(meta?.telefone)
    if (mudouUsuario) {
      const erro = await atualizarPerfil({ nome, email_comercial: emailComercial, telefone })
      if (erro) {
        setAvisoSalvar('Os dados da empresa foram salvos neste aparelho. Nome, telefone e e-mail comercial precisam de internet — tente de novo com sinal.')
        throw new Error(erro)
      }
    }
    setSujo(false)
    if (inicio) navegar('/')
  }

  async function confirmarTrocaDeSenha() {
    const invalida = validarNovaSenha(novaSenha, repetirSenha, senhaAtual)
    if (invalida) { setAvisoSenha({ texto: invalida, ok: false }); return }
    const conferencia = await conferirSenhaAtual(senhaAtual)
    if (conferencia !== 'ok') {
      setAvisoSenha({
        texto: conferencia === 'errada' ? 'A senha atual não confere.' : 'Sem conexão — tente de novo com internet.',
        ok: false,
      })
      return
    }
    const erro = await salvarNovaSenha(novaSenha)
    if (erro) { setAvisoSenha({ texto: erro, ok: false }); return }
    setAvisoSenha({ texto: 'Senha trocada ✓', ok: true })
    setSenhaAtual('')
    setNovaSenha('')
    setRepetirSenha('')
    setMostrarSenha(false)
  }

  function campoEmpresa(campo: CampoTexto, rotulo: string, extra: { tipo?: string; largo?: boolean; icone?: string } = {}) {
    return (
      <div className={extra.largo ? 'col-span-2' : 'col-span-2 min-[400px]:col-span-1'}>
        <label htmlFor={`emp-${campo}`} className={ROTULO}>{rotulo}</label>
        <div className="relative">
          {extra.icone && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true">{extra.icone}</span>}
          <input id={`emp-${campo}`} type={extra.tipo ?? 'text'} value={empresa[campo] ?? ''}
            onChange={(e) => mudarEmpresa({ [campo]: e.target.value })}
            className={`${CAMPO} ${extra.icone ? 'pl-9' : ''}`} />
        </div>
      </div>
    )
  }

  return (
    <section className="p-4 space-y-4 bg-slate-50 min-h-full">
      <div>
        <h2 className="text-xl font-bold text-slate-800">{inicio ? 'Cadastro inicial' : 'Meu perfil'}</h2>
        {inicio && (
          <p className="text-sm text-slate-500">
            Informe os dados da sua empresa e os seus dados de usuário para começar a utilizar o sistema.
          </p>
        )}
      </div>

      <Cartao testId="cartao-empresa" icone="🏢" titulo="Dados da empresa"
        subtitulo="Essas informações aparecerão nos documentos (PDF, pedidos, etc).">
        <QuadroDaImagem testId="imagem-empresa" imagem={empresa.logo} rotuloVazio="Escolher imagem"
          aoMudar={(logo) => mudarEmpresa({ logo })} />
        <div className="grid grid-cols-2 gap-3">
          {campoEmpresa('razao_social', 'Razão social / Nome da empresa', { largo: true })}
          {campoEmpresa('nome_fantasia', 'Nome fantasia')}
          {campoEmpresa('cnpj', 'CNPJ')}
          {campoEmpresa('email', 'E-mail da empresa', { tipo: 'email', icone: '✉' })}
          {campoEmpresa('telefone', 'Telefone comercial', { tipo: 'tel', icone: '☎' })}
          {campoEmpresa('endereco', 'Endereço', { largo: true, icone: '📍' })}
          {campoEmpresa('cep', 'CEP')}
          {campoEmpresa('municipio', 'Cidade')}
          <div className="col-span-2 min-[400px]:col-span-1">
            <label htmlFor="emp-uf" className={ROTULO}>Estado</label>
            <select id="emp-uf" value={empresa.uf ?? ''} onChange={(e) => mudarEmpresa({ uf: e.target.value })} className={CAMPO}>
              <option value="">—</option>
              {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
        </div>
      </Cartao>

      <Cartao testId="cartao-usuario" icone="👤" titulo="Seus dados de usuário"
        subtitulo="Esses dados serão usados para login e identificação no sistema.">
        <div>
          <label htmlFor="perfil-nome" className={ROTULO}>Nome completo</label>
          <input id="perfil-nome" value={nome} onChange={(e) => mudar(setNome)(e.target.value)} className={CAMPO} />
        </div>
        <div>
          <label htmlFor="perfil-telefone" className={ROTULO}>Telefone / WhatsApp</label>
          <input id="perfil-telefone" type="tel" value={telefone} onChange={(e) => mudar(setTelefone)(e.target.value)}
            placeholder="(11) 99999-0000" className={CAMPO} />
        </div>
        <div>
          <label htmlFor="perfil-email" className={ROTULO}>E-mail comercial</label>
          <input id="perfil-email" type="email" value={emailComercial}
            onChange={(e) => mudar(setEmailComercial)(e.target.value)} className={CAMPO} />
          {/* Em branco, o PDF continua usando o de login (09/09). */}
          <p className="text-xs text-slate-500 mt-1">Em branco, o PDF usa o e-mail de login.</p>
        </div>
        <div>
          <label htmlFor="perfil-login" className={ROTULO}>E-mail de login</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true">🔒</span>
            <input id="perfil-login" readOnly value={session?.user.email ?? ''}
              className={`${CAMPO} pl-9 bg-slate-50 text-slate-500`} />
          </div>
        </div>
        {!mostrarSenha ? (
          <button type="button" onClick={() => { setMostrarSenha(true); setAvisoSenha(null) }}
            className="text-sm border border-teal-700 text-teal-800 rounded-lg px-3 py-1.5">Trocar senha</button>
        ) : (
          <div className="space-y-2 rounded-xl border border-slate-200 p-3">
            <div>
              <label htmlFor="senha-atual" className={ROTULO}>Senha atual</label>
              <input id="senha-atual" type="password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} className={CAMPO} />
            </div>
            <div>
              <label htmlFor="senha-nova" className={ROTULO}>Nova senha</label>
              <input id="senha-nova" type="password" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} className={CAMPO} />
            </div>
            <div>
              <label htmlFor="senha-repetir" className={ROTULO}>Repetir nova senha</label>
              <input id="senha-repetir" type="password" value={repetirSenha} onChange={(e) => setRepetirSenha(e.target.value)} className={CAMPO} />
            </div>
            <p className="text-xs text-slate-500">Mínimo de 8 caracteres.</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => void confirmarTrocaDeSenha()}
                className="text-sm bg-teal-700 text-white rounded-lg px-3 py-1.5">Confirmar troca</button>
              <button type="button" onClick={() => { setMostrarSenha(false); setSenhaAtual(''); setNovaSenha(''); setRepetirSenha('') }}
                className="text-sm border border-slate-300 text-slate-600 rounded-lg px-3 py-1.5">Cancelar</button>
            </div>
          </div>
        )}
        {avisoSenha && (
          <p role="status" className={`text-sm ${avisoSenha.ok ? 'text-green-700' : 'text-red-700'}`}>{avisoSenha.texto}</p>
        )}
      </Cartao>

      {avisoSalvar && <p role="alert" className="text-sm text-amber-800">{avisoSalvar}</p>}
      <BotaoSalvar estado={estado} type="button" onClick={() => void executar(salvar)}>
        {inicio ? 'Salvar e continuar' : 'Salvar'}
      </BotaoSalvar>
      {inicio ? (
        <button type="button" onClick={() => { setSujo(false); navegar('/') }}
          className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 font-medium">
          Preencher depois
        </button>
      ) : (
        <button type="button" onClick={() => { void podeSair().then((ok) => { if (ok) navegar('/mais') }) }}
          className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 font-medium">
          Voltar
        </button>
      )}
    </section>
  )
}
